import {
  formatARS,
  PRICE_CHANGE_REASON_LABELS,
  type PriceBatchDto,
  type PriceChangeReason,
  type PriceHistoryQuery,
  type PriceRevertDto,
} from '@gl/shared';
import { ChevronLeft, ChevronRight, Eye, History, Layers, RotateCcw, SearchX, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { EmptyState } from '@/components/EmptyState';
import { Spinner } from '@/components/FullScreenLoader';
import { FormField } from '@/components/FormField';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { errorMessage } from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatDateTime, formatPct, shortBatch } from './format';
import { revertSummary, useBatches, useCatalog, useHistory, useRevertBatch } from './hooks';

const ALL = '__all';
const HISTORY_PAGE_SIZE = 20;
const BATCHES_PAGE_SIZE = 5;

const REASON_VARIANT: Record<PriceChangeReason, 'secondary' | 'default' | 'outline'> = {
  MANUAL: 'secondary',
  BULK: 'default',
  REVERT: 'outline',
};

export function ReasonChip({ reason }: { reason: PriceChangeReason }) {
  return <Badge variant={REASON_VARIANT[reason]}>{PRICE_CHANGE_REASON_LABELS[reason]}</Badge>;
}

function Pager({
  page,
  pageSize,
  total,
  onPage,
  busy,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (p: number) => void;
  busy?: boolean;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return null;
  return (
    <div className="flex items-center justify-between gap-2 pt-3 text-sm text-muted-foreground">
      <span>
        Página {page} de {pages} · {total} en total
      </span>
      <div className="flex items-center gap-1">
        {busy && <Spinner />}
        <Button variant="outline" size="icon" aria-label="Página anterior" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          <ChevronLeft />
        </Button>
        <Button variant="outline" size="icon" aria-label="Página siguiente" disabled={page >= pages} onClick={() => onPage(page + 1)}>
          <ChevronRight />
        </Button>
      </div>
    </div>
  );
}

export function HistoryTab({
  localId,
  onStartUpdate,
}: {
  localId: string;
  /** Lleva a la pestaña "Actualizar precios". */
  onStartUpdate?: () => void;
}) {
  const [batchPage, setBatchPage] = useState(1);
  const [filters, setFilters] = useState<Omit<PriceHistoryQuery, 'page' | 'pageSize'>>({});
  const [page, setPage] = useState(1);
  const [toRevert, setToRevert] = useState<PriceBatchDto | null>(null);
  const [result, setResult] = useState<PriceRevertDto | null>(null);

  const batches = useBatches(localId, { page: batchPage, pageSize: BATCHES_PAGE_SIZE });
  const batchOptions = useBatches(localId, { page: 1, pageSize: 50 });
  const query: PriceHistoryQuery = { ...filters, page, pageSize: HISTORY_PAGE_SIZE };
  const history = useHistory(localId, query);
  const catalog = useCatalog(localId);

  const revert = useRevertBatch(localId, { onSuccess: setResult });

  const itemOptions = useMemo(
    () =>
      (catalog.data?.categories ?? [])
        .flatMap((c) => c.items)
        .map((i) => ({ id: i.id, name: i.name }))
        .sort((a, b) => a.name.localeCompare(b.name, 'es')),
    [catalog.data],
  );

  const batchSelectOptions = useMemo(() => {
    const list = batchOptions.data?.items ?? [];
    if (filters.batchId && !list.some((b) => b.batchId === filters.batchId)) {
      return [{ batchId: filters.batchId, label: shortBatch(filters.batchId) }, ...list.map(batchLabel)];
    }
    return list.map(batchLabel);
  }, [batchOptions.data, filters.batchId]);

  const setFilter = (patch: Partial<typeof filters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };
  const hasFilters = Object.values(filters).some(Boolean);

  const closeRevert = () => {
    if (revert.isPending) return;
    setToRevert(null);
    setResult(null);
    revert.reset();
  };

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Lotes de cambios</CardTitle>
          <CardDescription>Cada actualización masiva queda agrupada en un lote que podés revertir.</CardDescription>
        </CardHeader>
        <CardContent>
          {batches.isPending ? (
            <div className="flex justify-center py-6">
              <Spinner className="size-5 text-muted-foreground" />
            </div>
          ) : batches.error ? (
            <p className="text-sm text-destructive">{errorMessage(batches.error)}</p>
          ) : batches.data.items.length === 0 ? (
            <EmptyState
              compact
              icon={Layers}
              title="Todavía no hiciste actualizaciones masivas"
              description="Subí o bajá varios precios de una (p. ej. +10 % a Helados) y el lote queda acá para revertirlo si hace falta."
              action={
                onStartUpdate && (
                  <Button size="sm" onClick={onStartUpdate}>
                    Actualizar precios
                  </Button>
                )
              }
            />
          ) : (
            <>
              <ul className="divide-y rounded-lg border">
                {batches.data.items.map((b) => (
                  <li key={b.batchId} className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center">
                    <div className="grid min-w-0 flex-1 gap-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <ReasonChip reason={b.reason} />
                        <span className="font-mono text-xs text-muted-foreground">{shortBatch(b.batchId)}</span>
                        {b.revertedByBatchId && <Badge variant="outline">Revertido</Badge>}
                      </div>
                      <p className="text-sm">
                        <span className="font-medium">{b.count === 1 ? '1 precio' : `${b.count} precios`}</span>
                        <span className="text-muted-foreground"> · {formatPct(b.avgDeltaPct)} promedio</span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDateTime(b.createdAt)}
                        {b.user && ` · ${b.user.name || b.user.email}`}
                        {b.note && ` · ${b.note}`}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setFilter({ batchId: b.batchId })}>
                        <Eye /> Ver cambios
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!!b.revertedByBatchId}
                        onClick={() => setToRevert(b)}
                      >
                        <RotateCcw /> Revertir
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
              <Pager
                page={batchPage}
                pageSize={BATCHES_PAGE_SIZE}
                total={batches.data.total}
                onPage={setBatchPage}
                busy={batches.isFetching}
              />
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Historial de precios</CardTitle>
          <CardDescription>Todos los cambios, manuales y masivos, del más nuevo al más viejo.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <FormField label="Lote">
              <Select value={filters.batchId ?? ALL} onValueChange={(v) => setFilter({ batchId: v === ALL ? undefined : v })}>
                <SelectTrigger aria-label="Filtrar por lote">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos</SelectItem>
                  {batchSelectOptions.map((b) => (
                    <SelectItem key={b.batchId} value={b.batchId}>
                      {b.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Producto">
              <Select value={filters.itemId ?? ALL} onValueChange={(v) => setFilter({ itemId: v === ALL ? undefined : v })}>
                <SelectTrigger aria-label="Filtrar por producto">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos</SelectItem>
                  {itemOptions.map((i) => (
                    <SelectItem key={i.id} value={i.id}>
                      {i.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Desde" htmlFor="history-from">
              <Input
                id="history-from"
                type="date"
                value={filters.from ?? ''}
                max={filters.to}
                onChange={(e) => setFilter({ from: e.target.value || undefined })}
              />
            </FormField>
            <FormField label="Hasta" htmlFor="history-to">
              <Input
                id="history-to"
                type="date"
                value={filters.to ?? ''}
                min={filters.from}
                onChange={(e) => setFilter({ to: e.target.value || undefined })}
              />
            </FormField>
          </div>
          {hasFilters && (
            <div>
              <Button variant="ghost" size="sm" onClick={() => setFilter({ batchId: undefined, itemId: undefined, from: undefined, to: undefined })}>
                <X /> Limpiar filtros
              </Button>
            </div>
          )}

          {history.isPending ? (
            <div className="flex justify-center py-6">
              <Spinner className="size-5 text-muted-foreground" />
            </div>
          ) : history.error ? (
            <p className="text-sm text-destructive">{errorMessage(history.error)}</p>
          ) : history.data.items.length === 0 ? (
            hasFilters ? (
              <EmptyState
                compact
                icon={SearchX}
                title="No hay cambios con esos filtros"
                description="Probá con otro producto o con otras fechas."
                action={
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setFilter({ batchId: undefined, itemId: undefined, from: undefined, to: undefined })}
                  >
                    <X /> Limpiar filtros
                  </Button>
                }
              />
            ) : (
              <EmptyState
                compact
                icon={History}
                title="Todavía no hay cambios de precio"
                description="Cada vez que cambies un precio, a mano o en masa, queda registrado acá con el antes y el después."
              />
            )
          ) : (
            <div>
              <Table className={cn(history.isFetching && 'opacity-60')}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="hidden md:table-cell">Fecha</TableHead>
                    <TableHead>Producto</TableHead>
                    <TableHead className="text-right">Precio</TableHead>
                    <TableHead className="hidden text-right sm:table-cell">Δ</TableHead>
                    <TableHead className="hidden sm:table-cell">Motivo</TableHead>
                    <TableHead className="hidden lg:table-cell">Usuario</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.data.items.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="hidden text-muted-foreground tabular-nums md:table-cell">
                        {formatDateTime(c.createdAt)}
                      </TableCell>
                      <TableCell className="whitespace-normal">
                        <p>{c.name}</p>
                        <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground md:hidden">
                          {formatDateTime(c.createdAt)}
                          <span className="sm:hidden">
                            <ReasonChip reason={c.reason} />
                          </span>
                        </p>
                        {c.batchId && (
                          <button
                            type="button"
                            className="font-mono text-xs text-muted-foreground hover:underline"
                            onClick={() => setFilter({ batchId: c.batchId ?? undefined })}
                          >
                            {shortBatch(c.batchId)}
                          </button>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        <span className="block text-xs text-muted-foreground line-through">{formatARS(c.oldPriceCents)}</span>
                        <span className="font-medium">{formatARS(c.newPriceCents)}</span>
                      </TableCell>
                      <TableCell
                        className={cn(
                          'hidden text-right tabular-nums sm:table-cell',
                          c.deltaPct !== null && c.deltaPct > 0 && 'text-success',
                          c.deltaPct !== null && c.deltaPct < 0 && 'text-destructive',
                        )}
                      >
                        {formatPct(c.deltaPct)}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <ReasonChip reason={c.reason} />
                      </TableCell>
                      <TableCell className="hidden text-muted-foreground lg:table-cell">
                        {c.user ? c.user.name || c.user.email : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pager
                page={page}
                pageSize={HISTORY_PAGE_SIZE}
                total={history.data.total}
                onPage={setPage}
                busy={history.isFetching}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!toRevert} onOpenChange={(o) => !o && closeRevert()}>
        <DialogContent>
          {result ? (
            <>
              <DialogHeader>
                <DialogTitle>{result.count > 0 ? 'Lote revertido' : 'No se revirtió nada'}</DialogTitle>
                <DialogDescription>{revertSummary(result)}.</DialogDescription>
              </DialogHeader>
              {result.skipped.length > 0 && (
                <div className="grid gap-2 text-sm">
                  <p className="font-medium">Se saltearon porque cambiaron después del lote:</p>
                  <ul className="grid max-h-60 gap-1 overflow-y-auto">
                    {result.skipped.map((s, i) => (
                      <li key={`${s.id ?? 'x'}-${i}`} className="flex justify-between gap-3 rounded-md bg-muted px-2 py-1">
                        <span>{s.name}</span>
                        <span className="text-muted-foreground tabular-nums">
                          {s.reason === 'DELETED'
                            ? 'Eliminado'
                            : `hoy ${formatARS(s.currentPriceCents ?? 0)} (el lote dejó ${formatARS(s.expectedPriceCents)})`}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <DialogFooter>
                <Button onClick={closeRevert}>Listo</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>¿Revertir el lote {toRevert && shortBatch(toRevert.batchId)}?</DialogTitle>
                <DialogDescription>
                  Los {toRevert?.count ?? 0} precios vuelven a su valor anterior. Si alguno cambió después del lote, lo
                  dejamos como está y te avisamos.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline" disabled={revert.isPending}>
                    Cancelar
                  </Button>
                </DialogClose>
                <Button disabled={revert.isPending} onClick={() => toRevert && revert.mutate(toRevert.batchId)}>
                  {revert.isPending && <Spinner />} Revertir
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function batchLabel(b: PriceBatchDto) {
  return {
    batchId: b.batchId,
    label: `${shortBatch(b.batchId)} · ${PRICE_CHANGE_REASON_LABELS[b.reason]} · ${formatDateTime(b.createdAt)}`,
  };
}
